import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import DescriptionIcon from '@mui/icons-material/Description';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InventoryIcon from '@mui/icons-material/Inventory';
import axiosInstance from '../../lib/axios';
import { palette, typography, theme } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';

interface B2BStatistics {
  totalResellers: number;
  activeResellers: number;
  pendingAccessRequests: number;
  totalQuotes: number;
  quotesThisMonth: number;
  acceptedQuotesThisMonth: number;
  totalB2bRevenue: number;
  revenueThisMonth: number;
  topProducts: Array<{
    productId: string;
    productName: string;
    totalQuantity: number;
    totalRevenue: number;
  }>;
  resellersByTier: Array<{
    tierName: string;
    count: number;
  }>;
}

const fetchB2BStatistics = async (): Promise<B2BStatistics> => {
  const response = await axiosInstance.get('/b2b-portal/admin/statistics');
  return response.data;
};

export const B2BDashboard = () => {
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: ['b2b-statistics'],
    queryFn: fetchB2BStatistics,
    refetchInterval: 60000, // Auto-refresh every minute
  });

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const cardStyles = {
    backgroundColor: colors.surface,
    border: `2px solid ${colors.border}`,
    borderRadius: theme.radius.sm,
    p: 2.5,
    position: 'relative',
    overflow: 'hidden',
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress sx={{ color: palette.primary }} />
        <Typography
          variant="body2"
          sx={{
            color: colors.textMuted,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.sm,
            textTransform: 'uppercase',
            letterSpacing: typography.letterSpacing.widest,
          }}
        >
          LOADING B2B DATA...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          sx={{
            borderRadius: theme.radius.sm,
            backgroundColor: alpha(palette.error, 0.1),
            color: palette.error,
            border: `2px solid ${palette.error}`,
            fontFamily: typography.fontFamily.mono,
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => refetch()}
              sx={{
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                fontFamily: typography.fontFamily.mono,
              }}
            >
              RETRY
            </Button>
          }
        >
          {error instanceof Error ? error.message : 'Failed to load B2B statistics'}
        </Alert>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box p={3}>
        <Alert
          severity="info"
          sx={{
            borderRadius: theme.radius.sm,
            backgroundColor: alpha(palette.info, 0.1),
            color: palette.info,
            border: `2px solid ${palette.info}`,
            fontFamily: typography.fontFamily.mono,
          }}
        >
          NO B2B DATA AVAILABLE
        </Alert>
      </Box>
    );
  }

  const conversionRate = stats.quotesThisMonth > 0
    ? Math.round((stats.acceptedQuotesThisMonth / stats.quotesThisMonth) * 100)
    : 0;

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <BusinessIcon sx={{ color: palette.primary, fontSize: 24 }} />
          <Typography
            variant="overline"
            sx={{
              color: palette.primary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            B2B RESELLER PORTAL
          </Typography>
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.extrabold,
            color: colors.textPrimary,
            letterSpacing: '-0.02em',
          }}
        >
          Partner Performance Dashboard
        </Typography>
      </Box>

      {/* Key Metrics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Total Resellers */}
        <Box
          sx={{
            ...cardStyles,
            ...animations.scaleIn(0.1),
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.primary,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <GroupsIcon sx={{ color: palette.primary, fontSize: 28 }} />
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                }}
              >
                TOTAL RESELLERS
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: colors.textPrimary,
                  lineHeight: 1,
                }}
              >
                {stats.totalResellers}
              </Typography>
            </Box>
          </Box>
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
            label={`${stats.activeResellers} active`}
            size="small"
            sx={{
              backgroundColor: alpha(palette.success, 0.1),
              color: palette.success,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              border: `1px solid ${palette.success}`,
            }}
          />
        </Box>

        {/* Pending Access Requests */}
        <Box
          sx={{
            ...cardStyles,
            ...animations.scaleIn(0.2),
            borderColor: stats.pendingAccessRequests > 0 ? palette.warning : colors.border,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.warning,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <PendingActionsIcon sx={{ color: palette.warning, fontSize: 28 }} />
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                }}
              >
                PENDING REQUESTS
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: stats.pendingAccessRequests > 0 ? palette.warning : colors.textPrimary,
                  lineHeight: 1,
                }}
              >
                {stats.pendingAccessRequests}
              </Typography>
            </Box>
          </Box>
          {stats.pendingAccessRequests > 0 && (
            <Button
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate('/app/access-requests?status=pending')}
              sx={{
                color: palette.warning,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.xs,
                fontFamily: typography.fontFamily.mono,
                border: `1px solid ${palette.warning}`,
                '&:hover': {
                  backgroundColor: alpha(palette.warning, 0.1),
                },
              }}
            >
              REVIEW
            </Button>
          )}
        </Box>

        {/* Quotes This Month */}
        <Box
          sx={{
            ...cardStyles,
            ...animations.scaleIn(0.3),
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.info,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <DescriptionIcon sx={{ color: palette.info, fontSize: 28 }} />
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                }}
              >
                QUOTES THIS MONTH
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: colors.textPrimary,
                  lineHeight: 1,
                }}
              >
                {stats.quotesThisMonth}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`${stats.acceptedQuotesThisMonth} accepted`}
              size="small"
              sx={{
                backgroundColor: alpha(palette.success, 0.1),
                color: palette.success,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                border: `1px solid ${palette.success}`,
              }}
            />
            <Chip
              label={`${conversionRate}% conversion`}
              size="small"
              sx={{
                backgroundColor: alpha(palette.primary, 0.1),
                color: palette.primary,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                border: `1px solid ${palette.primary}`,
              }}
            />
          </Box>
        </Box>

        {/* Revenue This Month */}
        <Box
          sx={{
            ...cardStyles,
            ...animations.scaleIn(0.4),
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.success,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <MonetizationOnIcon sx={{ color: palette.success, fontSize: 28 }} />
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                }}
              >
                B2B REVENUE (MONTH)
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.success,
                  lineHeight: 1,
                }}
              >
                {formatCurrency(stats.revenueThisMonth)}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
            }}
          >
            Total: {formatCurrency(stats.totalB2bRevenue)}
          </Typography>
        </Box>
      </Box>

      {/* Secondary Metrics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Top B2B Products */}
        <Box sx={{ ...cardStyles, ...animations.scaleIn(0.5) }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmojiEventsIcon sx={{ color: palette.warning, fontSize: 24 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                  textTransform: 'uppercase',
                  letterSpacing: typography.letterSpacing.wide,
                }}
              >
                TOP B2B PRODUCTS
              </Typography>
            </Box>
            <Button
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate('/app/products?tab=b2b')}
              sx={{
                color: palette.primary,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.xs,
                fontFamily: typography.fontFamily.mono,
              }}
            >
              VIEW ALL
            </Button>
          </Box>

          {!stats.topProducts || stats.topProducts.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.xs,
              }}
            >
              <InventoryIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
              <Typography
                variant="body2"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  textTransform: 'uppercase',
                }}
              >
                NO B2B SALES YET
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(stats.topProducts || []).slice(0, 5).map((product, index) => (
                <Box
                  key={product.productId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    backgroundColor: colors.surfaceElevated,
                    border: `1px solid ${colors.border}`,
                    borderLeft: `4px solid ${index === 0 ? palette.warning : palette.primary}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={`#${index + 1}`}
                      size="small"
                      sx={{
                        backgroundColor: index === 0 ? alpha(palette.warning, 0.1) : colors.surface,
                        color: index === 0 ? palette.warning : colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.bold,
                        border: `1px solid ${index === 0 ? palette.warning : colors.border}`,
                        minWidth: 40,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: typography.fontWeight.bold,
                          color: colors.textPrimary,
                          fontFamily: typography.fontFamily.display,
                        }}
                      >
                        {product.productName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                        }}
                      >
                        {product.totalQuantity} units sold
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.extrabold,
                      color: palette.success,
                    }}
                  >
                    {formatCurrency(product.totalRevenue)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Resellers by Tier */}
        <Box sx={{ ...cardStyles, ...animations.scaleIn(0.6) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <TrendingUpIcon sx={{ color: palette.info, fontSize: 24 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wide,
              }}
            >
              RESELLERS BY TIER
            </Typography>
          </Box>

          {!stats.resellersByTier || stats.resellersByTier.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.xs,
              }}
            >
              <GroupsIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
              <Typography
                variant="body2"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  textTransform: 'uppercase',
                }}
              >
                NO TIER DATA AVAILABLE
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(stats.resellersByTier || []).map((tier, index) => {
                const percentage = stats.totalResellers > 0
                  ? Math.round((tier.count / stats.totalResellers) * 100)
                  : 0;
                const tierColors = [palette.warning, palette.primary, palette.info, palette.success];
                const tierColor = tierColors[index % tierColors.length];

                return (
                  <Box key={tier.tierName}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: typography.fontWeight.bold,
                          color: colors.textPrimary,
                          fontFamily: typography.fontFamily.mono,
                          textTransform: 'uppercase',
                        }}
                      >
                        {tier.tierName}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: tierColor,
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.bold,
                        }}
                      >
                        {tier.count} ({percentage}%)
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 8,
                        backgroundColor: colors.surfaceElevated,
                        borderRadius: theme.radius.xs,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${percentage}%`,
                          height: '100%',
                          backgroundColor: tierColor,
                          transition: 'width 0.5s ease-out',
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Quick Actions */}
      <Box sx={{ ...cardStyles, ...animations.scaleIn(0.7) }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            textTransform: 'uppercase',
            letterSpacing: typography.letterSpacing.wide,
            mb: 3,
          }}
        >
          QUICK ACTIONS
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<BusinessIcon />}
            onClick={() => navigate('/app/resellers')}
            sx={{
              backgroundColor: palette.primary,
              color: '#FFFFFF',
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.sm,
              fontFamily: typography.fontFamily.mono,
              px: 3,
              py: 1.5,
              borderRadius: theme.radius.sm,
            }}
          >
            MANAGE RESELLERS
          </Button>
          <Button
            variant="outlined"
            startIcon={<PendingActionsIcon />}
            onClick={() => navigate('/app/access-requests')}
            sx={{
              borderColor: colors.border,
              color: colors.textPrimary,
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.sm,
              fontFamily: typography.fontFamily.mono,
              px: 3,
              py: 1.5,
              borderRadius: theme.radius.sm,
              '&:hover': {
                borderColor: palette.primary,
                backgroundColor: alpha(palette.primary, 0.1),
              },
            }}
          >
            ACCESS REQUESTS
          </Button>
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={() => navigate('/app/quotes')}
            sx={{
              borderColor: colors.border,
              color: colors.textPrimary,
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.sm,
              fontFamily: typography.fontFamily.mono,
              px: 3,
              py: 1.5,
              borderRadius: theme.radius.sm,
              '&:hover': {
                borderColor: palette.primary,
                backgroundColor: alpha(palette.primary, 0.1),
              },
            }}
          >
            VIEW QUOTES
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
